import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FooterAction } from '../../interfaces';
import {
  CustomerFileForm,
  customerFileSchema,
  CustomerOverview,
} from '../../types/customers';
import { FormData } from '../../components/global/FormSmallModal';
import { ACCEPTED_FILE_TYPES } from '../../constants/customers';
import useUploadCustomerFile from './useUploadCustomerFile';
import useBlockCustomer from './useBlockCustomer';
import useConfirm from '../useConfirm';
import useLoadingModal from '../global/useLoadingModal';
import useUser from '../global/useUser';
import { openCustomerChat } from '../../helpers/customers';
import { isUserAdminOrManager } from '../../utils/global/helper';
import {
  CustomerBlockScope,
  invalidateCustomerFiles,
} from '../../services/customers';
import { SURFACE_HOVER } from '../../components/customers/profile/colors';

interface Args {
  customer: CustomerOverview | null | undefined;
  customerId: string | null;
  onCustomerChanged: () => unknown;
}

const MENU_ITEM_SX = {
  display: 'block',
  padding: '11px 14px',
  borderRadius: '5px',
  color: 'text.primary',
  '&:hover': { backgroundColor: SURFACE_HOVER },
  typography: 'subtitle2',
};

interface Result {
  overflowActions: FooterAction[];
  isEditCustomerModalOpen: boolean;
  openEditCustomerModal: () => void;
  closeEditCustomerModal: () => void;
  isFileUploadModalOpen: boolean;
  openFileUploadModal: () => void;
  closeFileUploadModal: () => void;
  fileUploadForm: FormData<CustomerFileForm>;
}

function useCustomerActions({
  customer,
  customerId,
  onCustomerChanged,
}: Args): Result {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { user } = useUser();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();

  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);

  const { mutateAsync: uploadCustomerFile, isPending: isUploading } =
    useUploadCustomerFile({
      refetch: async () => {
        await Promise.all([
          onCustomerChanged(),
          customerId ? invalidateCustomerFiles(customerId) : null,
        ]);
      },
      onSuccess: () => setIsFileUploadModalOpen(false),
      onError: () => toast.error('Failed to upload file'),
    });

  const { mutateAsync: toggleCustomerBlock, isPending: isTogglingBlock } =
    useBlockCustomer({
      onSuccess: () => {
        onCustomerChanged();
      },
      onError: () => {
        toast.error('Failed to update customer block status');
      },
    });

  const customerName = customer
    ? `${customer.user.firstName || ''} ${customer.user.lastName || ''}`.trim()
    : '';

  const isManager = !!user?.role && isUserAdminOrManager(user.role);
  const canShopBlock = isManager && user?.business?.type === 'STUDIO';
  const blockScope: CustomerBlockScope = canShopBlock ? 'SHOP' : 'ARTIST';
  const isScopedBlocked = canShopBlock
    ? !!customer?.shopBlocked
    : !!customer?.artistBlocked;

  const onOpenCustomerChat = useCallback(async () => {
    if (!customer || !user?.business) return;
    await openCustomerChat({
      openLoadingModal,
      closeLoadingModal,
      navigate,
      customerId: customer.customerId,
      user,
    });
  }, [closeLoadingModal, customer, navigate, openLoadingModal, user]);

  const handleToggleBlock = useCallback(async () => {
    if (!customer || !user?.business?.id || isTogglingBlock) return;

    const willBlock = !isScopedBlocked;
    const scopeLabel = canShopBlock ? 'for the shop' : 'for you';
    const confirmed = await confirm({
      title: willBlock
        ? `Block customer ${scopeLabel}`
        : `Unblock customer ${scopeLabel}`,
      message: willBlock
        ? canShopBlock
          ? `Are you sure you want to block ${customerName} for the entire shop? They will not be able to book or send chat messages to this business.`
          : `Are you sure you want to block ${customerName} for you? They will not be able to book or send chat messages to you.`
        : `Are you sure you want to unblock ${customerName} ${scopeLabel}?`,
      positiveText: willBlock ? 'Block' : 'Unblock',
    });
    if (!confirmed) return;

    try {
      openLoadingModal({
        header: willBlock ? 'Blocking customer...' : 'Unblocking customer...',
        message: 'Please wait',
      });
      await toggleCustomerBlock({
        customerId: customer.customerId,
        businessId: user.business.id,
        scope: blockScope,
        block: willBlock,
      });
      toast.success(
        willBlock
          ? canShopBlock
            ? 'Customer has been blocked for the shop'
            : 'Customer has been blocked for you'
          : canShopBlock
          ? 'Customer has been unblocked for the shop'
          : 'Customer has been unblocked for you',
      );
    } catch {
      // Error toast handled by useBlockCustomer onError
    } finally {
      closeLoadingModal();
    }
  }, [
    blockScope,
    canShopBlock,
    closeLoadingModal,
    confirm,
    customer,
    customerName,
    isScopedBlocked,
    isTogglingBlock,
    openLoadingModal,
    toggleCustomerBlock,
    user?.business?.id,
  ]);

  const overflowActions = useMemo(
    () =>
      customer
        ? [
            {
              label: 'Upload file for customer',
              action: () => setIsFileUploadModalOpen(true),
              sx: MENU_ITEM_SX,
            },
            {
              label: 'Open chat',
              action: onOpenCustomerChat,
              sx: MENU_ITEM_SX,
            },
            {
              label: 'Edit customer',
              action: () => setIsEditCustomerModalOpen(true),
              sx: MENU_ITEM_SX,
            },
            {
              label: isScopedBlocked
                ? canShopBlock
                  ? 'Unblock for shop'
                  : 'Unblock for me'
                : canShopBlock
                ? 'Block for shop'
                : 'Block for me',
              action: handleToggleBlock,
              sx: { ...MENU_ITEM_SX, color: 'error.dark' },
              dividerBefore: true,
            },
          ]
        : [],
    [
      canShopBlock,
      customer,
      handleToggleBlock,
      isScopedBlocked,
      onOpenCustomerChat,
    ],
  );

  const fileUploadForm: FormData<CustomerFileForm> = {
    initialValues: {
      file: null as any,
    },
    inputRows: [
      [
        {
          inputType: 'file',
          label: 'Upload file',
          name: 'file',
          accept: ACCEPTED_FILE_TYPES,
          acceptLabel: 'Image, PDF',
          acceptLabelNewLine: true,
          acceptLabelSx: {
            textAlign: 'center',
          },
          disabled: isUploading,
        },
      ],
    ],
    onSubmit: async values => {
      if (!customerId || !values.file) return;
      await uploadCustomerFile({
        customerId,
        file: values.file,
      });
    },
    submitButtonText: 'Upload',
    title: 'Upload file for customer',
    validationSchema: customerFileSchema,
  };

  return {
    overflowActions,
    isEditCustomerModalOpen,
    openEditCustomerModal: () => setIsEditCustomerModalOpen(true),
    closeEditCustomerModal: () => setIsEditCustomerModalOpen(false),
    isFileUploadModalOpen,
    openFileUploadModal: () => setIsFileUploadModalOpen(true),
    closeFileUploadModal: () => setIsFileUploadModalOpen(false),
    fileUploadForm,
  };
}

export default useCustomerActions;
